// The element currently carrying the suppress listeners, or null while picking is allowed. Keyed by the
// container rather than a boolean: the viewer is disposed and recreated on every project switch, and a
// flag that outlived the old canvas left the new one unguarded while still reporting "blocked", with the
// old container kept alive by its listeners (RTLCA-5722).
let guarded: HTMLElement | null = null;

const suppress = (e: Event) => {
  e.stopImmediatePropagation();
  e.preventDefault();
};

function getContainer(viewer: any): HTMLElement | null {
  // Try common spots where the viewer keeps its DOM element
  return viewer?.getContainer?.() ?? viewer?.domElement ?? viewer?.renderer?.domElement ?? null;
}

const addListeners = (el: HTMLElement, blockHover: boolean) => {
  el.addEventListener("click", suppress, true);
  el.addEventListener("dblclick", suppress, true);
  el.addEventListener("pointerup", suppress, true);
  if (blockHover) el.addEventListener("pointermove", suppress, true);
};

const removeListeners = (el: HTMLElement) => {
  el.removeEventListener("click", suppress, true);
  el.removeEventListener("dblclick", suppress, true);
  el.removeEventListener("pointerup", suppress, true);
  el.removeEventListener("pointermove", suppress, true);
};

/**
 * Block user picking (click-based selection) while keeping camera controls.
 * By default blocks click/dblclick and pointerup; optionally block hover, too.
 */
export function blockPicking(viewer: any, opts: { blockHover?: boolean } = {}) {
  const el = getContainer(viewer);
  if (!el) return;
  if (guarded === el) return;
  // A different container is a previous viewer's: release it before guarding the live one.
  if (guarded) removeListeners(guarded);
  addListeners(el, !!opts.blockHover);
  guarded = el;
}

/** Re-enable picking */
export function unblockPicking() {
  if (!guarded) return;
  removeListeners(guarded);
  guarded = null;
}

/** For disposeViewer: the container goes with the viewer, so nothing may stay attached to it. */
export function resetPickGuard() {
  unblockPicking();
}

export function isPickingBlocked() {
  return guarded !== null;
}
